import { RootEngine } from 'flume'
import config from '../Designer2/logicTypes/fieldFlumeConfig'
import store from '../../store'
import isBefore from 'date-fns/isBefore'
import isAfter from 'date-fns/isAfter'
import isEqual from 'date-fns/isEqual'

const getFieldById = (fieldId, context = {}) => {
  const fields = context.fields || store.getState().wizard.wizard.fields
  const field = fields[fieldId]
  if (field) {
    if (field.parent.type === 'REPEATER' && !context.isSavingRepeater) {
      const currentRowIndex = context.currentRepeaterRowIndex || 0
      const parentRepeater = fields[field.parent.id]
      const currentRow = parentRepeater.value[currentRowIndex]
      if (parentRepeater && currentRow && !context.isRenderingFieldInRepeater) {
        return {
          ...field,
          value: currentRow[field.fieldId],
        }
      } else {
        return field
      }
    } else {
      return field
    }
  } else {
    return field
  }
}

export const engine = new RootEngine(
  config,
  (type, data) => {
    switch (type) {
      case 'number':
        return data.number
      case 'boolean':
        return !!data.boolean
      case 'string':
        return data.string
      case 'fieldId':
        return data.fieldId
      case 'optionFieldId':
        return data.optionFieldId
      case 'checkboxFieldId':
        return data.checkboxFieldId
      case 'textboxFieldId':
        return data.textboxFieldId
      case 'uploadFieldId':
        return data.uploadFieldId
      case 'uploadAndSubmitFieldId':
        return data.uploadAndSubmitFieldId
      case 'nameQualifiedOrgFieldId':
        return data.nameQualifiedOrgFieldId
      case 'dateFieldId':
        return data.dateFieldId
      case 'entityNameFieldId':
        return data.entityNameFieldId
      case 'repeaterFieldId':
        return data.repeaterFieldId
      case 'formationLocaleFieldId':
        return data.formationLocaleFieldId
      case 'checkboxState':
        return data.checkboxState
      case 'fieldOptionValues':
        return data.values
      case 'businessRules':
        return data.businessRules
      case 'booleanAppSetting':
        return data.booleanAppSetting
      case 'stringAppSetting':
        return data.stringAppSetting
      case 'numberAppSetting':
        return data.numberAppSetting
      case 'globalBoolean':
        return data.globalBoolean
      case 'globalString':
        return data.globalString
      case 'globalNumber':
        return data.globalNumber
      case 'date':
        return data.date
      default:
        return data
    }
  },
  (node, inputValues, nodeType, context) => {
    switch (node.type) {
      case 'user':
        const user = store.getState().auth
        return {
          loggedIn: user.loggedIn,
          isAdmin: user.USER_GROUP === 'ADMIN' || user.USER_GROUP === 'ADMIN_WEB',
          firstName: user.FIRST_NAME,
          lastName: user.LAST_NAME,
          email: user.USERNAME,
        }
      case 'number':
        return { number: inputValues.number }
      case 'boolean':
        return { boolean: inputValues.boolean }
      case 'string':
        return { string: inputValues.string }
      case 'date':
        return { date: inputValues.date }
      case 'isBeforeDate':
        if (
          inputValues.date &&
          inputValues.date.length >= 10 &&
          inputValues.isBeforeDate &&
          inputValues.isBeforeDate.length >= 10
        ) {
          return {
            result: isBefore(new Date(inputValues.date), new Date(inputValues.isBeforeDate)),
          }
        } else {
          return { result: false }
        }
      case 'isAfterDate':
        if (
          inputValues.date &&
          inputValues.date.length >= 10 &&
          inputValues.isAfterDate &&
          inputValues.isAfterDate.length >= 10
        ) {
          return { result: isAfter(new Date(inputValues.date), new Date(inputValues.isAfterDate)) }
        } else {
          return { result: false }
        }
      case 'datesEqual':
        if (
          inputValues.date1 &&
          inputValues.date1.length >= 10 &&
          inputValues.date2 &&
          inputValues.date2.length >= 10
        ) {
          return { result: isEqual(new Date(inputValues.date1), new Date(inputValues.date2)) }
        } else {
          return { result: false }
        }
      case 'and':
        return { result: inputValues.bool1 && inputValues.bool2 }
      case 'or':
        return { result: inputValues.bool1 || inputValues.bool2 }
      case 'reverseBoolean':
        return { result: !inputValues.boolean }
      case 'stringSwitch':
        return { result: inputValues.test ? inputValues.stringIfTrue : inputValues.stringIfFalse }
      case 'stringJoin':
        return { result: `${inputValues.string1}${inputValues.string2}` }
      case 'stringEquals': {
        const { string1 = '', string2 = '' } = inputValues
        return {
          result: inputValues.caseSensitive
            ? string1 === string2
            : string1.toLowerCase() === string2.toLowerCase(),
        }
      }
      case 'stringContains': {
        const { string1 = '', string2 = '' } = inputValues
        let result
        if (inputValues.caseSensitive) {
          result = string1.includes(string2)
        } else {
          result = string1.toLowerCase().includes(string2.toLowerCase())
        }
        return { result }
      }
      case 'stringRegex': {
        const { string = '', regex = '', flags = '' } = inputValues
        const rgx = new RegExp(regex, flags)
        const matches = string.match(rgx)
        if (matches) {
          return { result: !!matches.length }
        } else {
          return { result: false }
        }
      }
      case 'field': {
        const fields = context.fields || store.getState().wizard.wizard.fields
        const field = fields[inputValues.fieldId]
        return field ? resolveLogic(field, fields) : {}
      }
      case 'valueEqualsOneOfOptions': {
        const field = getFieldById(inputValues.optionFieldId, context)
        let value, isMultiComplete
        if (field) {
          value = field.value
          isMultiComplete = field.type === 'MultiComplete' || field.type === 'ServerMultiComplete'
        }
        return {
          result: isMultiComplete
            ? value.some((v) => inputValues.fieldOptionValues.includes(v))
            : inputValues.fieldOptionValues.includes(value),
        }
      }
      case 'checkboxIsInState': {
        const field = getFieldById(inputValues.checkboxFieldId, context)
        let value = false
        if (field) {
          value = field.value
        }
        return {
          result: inputValues.checkboxState === 'checked' ? value === true : value === false,
        }
      }
      case 'textbox': {
        const field = getFieldById(inputValues.textboxFieldId, context)
        let output = { value: '', charCount: 0 }
        if (field) {
          output.value = field.value
          output.charCount = field.value.length
        }
        return output
      }
      case 'uploadAndSubmit': {
        const field = getFieldById(inputValues.uploadAndSubmitFieldId, context)
        let output = { isOnline: false, isMailing: false, isSignatures: false }
        if (field) {
          output.isOnline = field.value.method === 'online'
          output.isMailing = field.value.method === 'mail'
          output.isSignatures = field.value.method === 'signer'
        }
        return output
      }
      case 'file': {
        const field = getFieldById(inputValues.uploadFieldId, context)
        let output = { numAttachments: 0 }
        if (field) {
          output.numAttachments = field.value.length || 0
        }
        return output
      }
      case 'dateInput': {
        const field = getFieldById(inputValues.dateFieldId, context)
        return { date: field.value }
      }
      case 'entityNameField': {
        const field = getFieldById(inputValues.entityNameFieldId, context)
        if (field) {
          return {
            isReserved: field.value.reservedYN === 'Y',
            reservedName: field.value.reservedName,
            entityName: field.value.val1,
            confirmedEntityName: field.value.val2,
            additionalNameNeeded: field.value.additionalYN === 'Y',
            additionalName: field.value.additionalName || '',
            consentNeeded: field.value.consentYN === 'Y',
            numConsents: field.value.numConsents ? parseInt(field.value.numConsents, 10) : 0,
          }
        }
        return {
          isReserved: false,
          reservedName: '',
          entityName: '',
          confirmedEntityName: '',
          additionalNameNeeded: false,
          consentNeeded: false,
          numConsents: 0,
        }
      }
      case 'nameQualifiedOrgField': {
        const field = getFieldById(inputValues.nameQualifiedOrgFieldId, context)
        const isIndividual = field.value.ORG_TYPE === '0'
        const isQualifiedOrg = field.value.ORG_TYPE === '1'
        const isUnqualifiedOrg = field.value.ORG_TYPE === '2'
        const output = {
          isIndividual,
          firstName: isIndividual ? field.value.FIRST_NAME : '',
          middleName: isIndividual ? field.value.MIDDLE_NAME : '',
          lastName: isIndividual ? field.value.LAST_NAME : '',
          isQualifiedOrg,
          hasSelectedQualifiedOrg: isQualifiedOrg ? !!field.value.SELECTED : false,
          isUnqualifiedOrg,
          unqualifiedOrg: isUnqualifiedOrg ? field.value.ORG_NAME : '',
        }
        return output
      }
      case 'formationLocaleField': {
        const field = getFieldById(inputValues.formationLocaleFieldId, context)
        const isStateType = field.value.FORMATION_TYPE === 'STATE'
        const isCountryType = field.value.FORMATION_TYPE === 'COUNTRY'
        const isOtherType = field.value.FORMATION_TYPE === 'OTHER'
        return {
          isStateType,
          isCountryType,
          isOtherType,
          STATE: isStateType ? field.value.STATE : '',
          COUNTRY: isCountryType ? field.value.COUNTRY : '',
          OTHER: isOtherType ? field.value.OTHER : '',
        }
      }
      case 'repeater': {
        const field = getFieldById(inputValues.repeaterFieldId, context)
        let output = { numRows: 0 }
        if (field) {
          output.numRows = field.value.length
        }
        return output
      }
      case 'businessRules': {
        return { businessRules: inputValues.businessRules }
      }
      case 'businessRulesSwitch': {
        return { businessRules: inputValues.test ? inputValues.rules1 : inputValues.rules2 }
      }
      case 'booleanAppSetting': {
        const setting = store.getState().appSettings[inputValues.booleanAppSetting]
        return { value: setting ? setting.VALUE : false }
      }
      case 'stringAppSetting': {
        const setting = store.getState().appSettings[inputValues.stringAppSetting]
        return { value: setting ? setting.VALUE : '' }
      }
      case 'wizardSettings': {
        const wizard = store.getState().wizard.wizard
        return {
          title: wizard.title,
          description: wizard.description,
          formTitle: wizard.formTitle,
          feeDescription: wizard.feeDescription,
        }
      }
      case 'globalBoolean': {
        const variable =
          store.getState().wizard.wizard.globalVariables.boolean.variables[
            inputValues.globalBoolean
          ]
        if (variable !== undefined) {
          const value = engine.resolveRootNode(variable.nodes).value
          return { value }
        }
        return { value: false }
      }
      case 'globalString': {
        const variable =
          store.getState().wizard.wizard.globalVariables.string.variables[inputValues.globalString]
        if (variable !== undefined) {
          const value = engine.resolveRootNode(variable.nodes).value
          return { value }
        }
        return { value: '' }
      }
      case 'globalNumber': {
        const variable =
          store.getState().wizard.wizard.globalVariables.number.variables[inputValues.globalNumber]
        if (variable !== undefined) {
          const value = engine.resolveRootNode(variable.nodes).value
          return { value }
        }
        return { value: 0 }
      }
      case 'metadataValue': {
        const meta = store.getState().wizard.wizard.meta
        const value = meta[inputValues.key]
        return { value: value !== undefined ? value : '' }
      }
      case 'addNumbers': {
        return { result: inputValues.num1 + inputValues.num2 }
      }
      case 'subtractNumbers': {
        return { result: inputValues.num1 - inputValues.num2 }
      }
      case 'multiplyNumbers': {
        return { result: inputValues.num1 * inputValues.num2 }
      }
      case 'divideNumbers': {
        return { result: inputValues.num1 / inputValues.num2 }
      }
      case 'greaterThanNumber': {
        return { result: inputValues.num1 > inputValues.num2 }
      }
      case 'lessThanNumber': {
        return { result: inputValues.num1 < inputValues.num2 }
      }
      case 'numbersEqual': {
        return { result: inputValues.num1 === inputValues.num2 }
      }
      case 'numberToString': {
        return { result: String(inputValues.num) }
      }
      case 'stringToNumber': {
        const number = Number(inputValues.string)
        const succeeded = !isNaN(number)
        return { result: succeeded ? number : 0, succeeded }
      }
      default:
        return undefined
    }
  }
)

const resolveLogic = (field, context) => {
  const logic = field.logicNodes
  if (Object.keys(logic).length) {
    const attrs = engine.resolveRootNode(logic, {
      onlyResolveConnected: true,
      context,
    })
    return { ...field, ...attrs }
  } else {
    return field
  }
}

export default resolveLogic



// WEBPACK FOOTER //
// ./src/components/Wizard2/resolveFieldLogic.js